/**
 * Metric Prompt Viewer Component
 * Modal for viewing built-in metric prompts (read-only)
 */

import { useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { useI18n } from '@/i18n';
import { useMetricPrompt } from '../hooks/useEvalMetrics';
import type { MetricDefinition } from '@/types/evalScenarios';

interface MetricPromptViewerProps {
  metric: MetricDefinition;
  isOpen: boolean;
  onClose: () => void;
}

export function MetricPromptViewer({ metric, isOpen, onClose }: MetricPromptViewerProps) {
  const { messages } = useI18n();
  const t = messages.evalScenarios.metricDefinitions;
  const pv = t?.promptViewer;
  const tc = messages.common;

  const { data: promptData, isLoading } = useMetricPrompt(isOpen ? metric.name : null);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const thresholdText = metric.threshold_direction === 'lower'
    ? `≤ ${(metric.default_threshold * 100).toFixed(0)}%`
    : `≥ ${(metric.default_threshold * 100).toFixed(0)}%`;

  const thresholdDirection = metric.threshold_direction === 'lower'
    ? t?.lowerBetter || 'Lower is better'
    : t?.higherBetter || 'Higher is better';

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🔒</span>
              <div>
                <h2 className="text-lg font-medium text-gray-900">
                  {metric.display_name}
                </h2>
                <span className="text-sm text-gray-500 font-mono">
                  {metric.name}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <span className="text-xl text-gray-500">×</span>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Metadata */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  {pv?.type || 'Type'}
                </label>
                <span className="inline-flex items-center gap-1 text-sm bg-blue-100 text-blue-700 px-2 py-1 rounded">
                  <span>📦</span>
                  {t?.systemMetric || 'System Metric'}
                </span>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  {pv?.source || 'Source'}
                </label>
                <span className="text-sm text-gray-700">
                  DeepEval {metric.builtin_metric}
                </span>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  {pv?.threshold || 'Default Threshold'}
                </label>
                <span className="text-sm text-gray-700">
                  {thresholdText} ({thresholdDirection})
                </span>
              </div>
            </div>

            {/* Description */}
            {metric.description && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  {tc.description}
                </label>
                <p className="text-sm text-gray-700">
                  {metric.description}
                </p>
              </div>
            )}

            {/* Prompt */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-2">
                {pv?.prompt || 'Evaluation Prompt'}
              </label>
              {isLoading ? (
                <div className="animate-pulse h-40 bg-gray-100 rounded-lg" />
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-[400px] overflow-y-auto">
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                    {promptData?.prompt || metric.builtin_prompt || 'Prompt not available'}
                  </pre>
                </div>
              )}
            </div>

            {/* Note */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <span className="text-lg">ℹ️</span>
                <div className="text-sm text-amber-800">
                  {t?.builtinPromptNote ||
                    'This is a DeepEval built-in metric. The prompt is maintained by the framework and cannot be modified. To customize evaluation logic, create a new GEval custom metric.'}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
            <Button variant="secondary" onClick={onClose}>
              {tc.close}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
